import { useSyncExternalStore } from "react";

/**
 * Dependency-free replacement for Apollo Client's `makeVar` / `useReactiveVar`.
 *
 * The app pulled in the entire `@apollo/client` package ONLY for these
 * reactive-variable state primitives — there is no GraphQL usage anywhere
 * (zero useQuery/useMutation/gql, no ApolloProvider). This shim provides the
 * exact subset the codebase uses so Apollo can be removed entirely (data-stack
 * consolidation):
 *
 *   const v = makeVar(initial);  // create
 *   v();                         // read
 *   v(next);                     // write + notify subscribers
 *   useReactiveVar(v);           // subscribe a component to v
 *
 * Backed by React's `useSyncExternalStore`, so reads are tearing-free and a
 * write re-renders exactly the components that read the var.
 */
export interface ReactiveVar<T> {
  // Matches @apollo/client's ReactiveVar call signature exactly so swapping the
  // import introduces no type changes: read with v(), write with v(next).
  (newValue?: T): T;
}

interface InternalReactiveVar<T> extends ReactiveVar<T> {
  /** Subscribe to changes; returns an unsubscribe fn. Used by useReactiveVar. */
  __subscribe(onStoreChange: () => void): () => void;
}

export function makeVar<T>(initialValue: T): ReactiveVar<T> {
  let value = initialValue;
  const subscribers = new Set<() => void>();

  const rv = function reactiveVar(...args: [T?]): T {
    if (args.length > 0) {
      value = args[0] as T;
      // Notify on every write (Apollo's makeVar does likewise); identical
      // snapshots are deduped by useSyncExternalStore so no spurious renders.
      subscribers.forEach((notify) => notify());
    }
    return value;
  } as InternalReactiveVar<T>;

  rv.__subscribe = (onStoreChange: () => void) => {
    subscribers.add(onStoreChange);
    return () => {
      subscribers.delete(onStoreChange);
    };
  };

  return rv;
}

export function useReactiveVar<T>(rv: ReactiveVar<T>): T {
  const internal = rv as InternalReactiveVar<T>;
  const read = () => rv();
  return useSyncExternalStore(internal.__subscribe, read, read);
}
